import { useAssetOverrides } from '@/hooks/useAssetOverrides';
import ScrollReveal from '@/components/ScrollReveal';
import { Star, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import originalZeusSunset from '@/assets/original-zeus-sunset.jpg';
import balconyViewSivota from '@/assets/balcony-view-sivota.jpg';

const reviews = [
  { name: 'Maria K.', date: 'August 2025', location: 'Athens, Greece', title: 'FANTASTIC VIEW', text: 'Fantastic view, amazing space with excellent services!!! What you dream about for your summer. Our experience at the panoramic apartments was incredible! The location is beautiful, and the view from the apartment towards Corfu and the endless blue is breathtaking. The hosts were very friendly. We will definitely visit again!', rating: 5 },
  { name: 'James R.', date: 'July 2025', location: 'London, UK', title: 'OUTSTANDING', text: 'Fantastic view, amazing space with excellent services!!! Our experience at the panoramic apartments was incredible! The location is beautiful, and the view from the apartment towards Corfu and the endless blue is breathtaking. The hosts were very friendly and provided us with all the necessary information.', rating: 5 },
  { name: 'Sophie L.', date: 'September 2025', location: 'Paris, France', title: 'FANTASTIC', text: 'We had a fantastic time at the panoramic apartments! The apartments are very spacious, and the kitchen is fully equipped. The view is simply stunning, with the endless blue sea and Corfu island in the distance. The location is quiet and peaceful. Highly recommended!', rating: 5 },
  { name: 'Andreas P.', date: 'June 2025', location: 'Thessaloniki, Greece', text: 'We had the most wonderful stay at Zeus apartment. The space is generous, the decor is tasteful, and waking up to that view every morning was magical. Highly recommended for couples and families alike.', rating: 5 },
  { name: 'Claire D.', date: 'August 2025', location: 'Brussels, Belgium', text: 'From the moment we arrived, we knew this was special. The hosts were incredibly welcoming, the apartment had everything we needed, and Sivota itself is one of the most beautiful places we have ever visited.', rating: 5 },
  { name: 'Thomas M.', date: 'July 2025', location: 'Munich, Germany', text: 'A hidden gem in Greece! The Hera apartment was beyond our expectations. Clean, modern, beautifully furnished with a spectacular sea view. The perfect base for exploring the area. We will definitely be back.', rating: 5 },
];

export default function Reviews() {
  const { img, extras } = useAssetOverrides();
  const { t } = useLanguage();

  return (
    <div>
      <section className="relative h-[75vh] flex items-end overflow-hidden">
        <img src={img(originalZeusSunset)} alt="Sunset from Sivota Royal terrace" className="absolute inset-0 w-full h-full object-cover hero-image-bright" width={1920} height={1080} fetchPriority="high" style={{ animation: 'heroZoom 25s ease-out forwards' }} />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative z-10 section-padding pb-16 lg:pb-24 w-full max-w-3xl">
          <p className="text-white/80 tracking-[0.4em] uppercase text-xs font-sans mb-4 drop-shadow-md">{t('testimonials')}</p>
          <h1 className="font-serif text-5xl md:text-7xl font-light text-white drop-shadow-lg">{t('guestReviews')}</h1>
        </div>
      </section>

      <section className="bg-secondary py-16">
        <ScrollReveal>
          <div className="text-center">
            <div className="flex justify-center gap-2 mb-4" aria-label="5 stars rating">
              {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={24} className="fill-golden text-golden" aria-hidden="true" />)}
            </div>
            <p className="font-serif text-5xl font-light text-foreground mb-2">9.2</p>
            <p className="text-muted-foreground text-sm tracking-wider uppercase">{t('exceptionalBooking')}</p>
          </div>
        </ScrollReveal>
      </section>

      <section className="section-padding py-20 lg:py-28">
        <ScrollReveal>
          <div className="max-w-3xl mx-auto text-center">
            <Quote size={32} className="text-golden/40 mx-auto mb-6" aria-hidden="true" />
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl font-light text-foreground leading-relaxed mb-6">{t('featuredQuote')}</p>
            <div className="w-12 h-px bg-golden mx-auto mb-4" />
            <p className="text-muted-foreground text-sm">{t('featuredQuoteAuthor')}</p>
          </div>
        </ScrollReveal>
      </section>

      <section className="section-padding pb-24 lg:pb-36">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {reviews.map((r, i) => (
            <ScrollReveal key={i} delay={i * 0.05}>
              <div className="bg-secondary p-8 lg:p-10 rounded-sm border border-border/40 h-full flex flex-col">
                <div className="flex gap-1 mb-4" aria-label={`${r.rating} out of 5 stars`}>
                  {Array.from({ length: r.rating }).map((_, j) => <Star key={j} size={14} className="fill-golden text-golden" aria-hidden="true" />)}
                </div>
                {r.title && <p className="font-serif text-lg font-medium text-foreground mb-3">{r.title}</p>}
                <p className="text-foreground/80 leading-relaxed mb-6 text-sm flex-1">"{r.text}"</p>
                <div>
                  <p className="font-serif text-lg text-foreground">{r.name}</p>
                  <p className="text-muted-foreground text-xs mt-0.5">{r.location} · {r.date}</p>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      <section className="relative py-28 lg:py-36">
        <img src={img(balconyViewSivota)} alt="" className="absolute inset-0 w-full h-full object-cover hero-image-bright" loading="lazy" width={1920} height={1080} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-black/10" />
        <div className="relative z-10 text-center section-padding">
          <ScrollReveal>
            <p className="text-white/80 tracking-[0.3em] uppercase text-xs font-sans mb-6">{t('joinOurGuests')}</p>
            <h2 className="font-serif text-4xl md:text-6xl font-light text-white mb-6 drop-shadow-lg">{t('createYourStory')}</h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/booking"
                className="bg-accent text-accent-foreground px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-accent/90 transition-colors">
                {t('checkAvailability')}
              </Link>
              <Link to="/booking" className="border border-white/40 text-white px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-white/10 transition-colors">
                {t('sendEnquiry')}
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
